import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import LeadForm from 'components/forms/LeadForm';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useIndustries } from 'services/industryService';

const PublicLeadForm = () => {
    const [searchParams] = useSearchParams();
    const { industries: industriesData, loading: industriesLoading } = useIndustries();
    const [offer, setOffer] = useState<any>(null);
    const [template, setTemplate] = useState<any>(null);
    const [templateStyle, setTemplateStyle] = useState<any>(null);
    const [formBuilder, setFormBuilder] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isSideSellerMode, setIsSideSellerMode] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const sellerId = searchParams.get('sellerId');
    const offerId = searchParams.get('offerId');
    const templateId = searchParams.get('templateId');
    const sideSellerId = searchParams.get('sideSellerId');

    useEffect(() => {
        const fetchOfferAndTemplate = async () => {
            try {
                setIsLoading(true);
                setError(null);

                // Fetch formBuilder from API
                const formBuilderRes = await axios.get(`${CONFIG.apiUrl}/form-field`);
                // API returns { results: [...] }, but LeadForm expects { fields: [...] }
                setFormBuilder({ fields: formBuilderRes.data.results || [] });

                // Mode 1: Template-only (side-seller) mode
                if (templateId && !offerId) {
                    setIsSideSellerMode(true);
                    const templateRes = await axios.get(`${CONFIG.apiUrl}/form-template/${templateId}`);
                    const templateData = templateRes.data;
                    setTemplate(templateData);
                    setTemplateStyle(templateData);

                    // For side-seller mode, use mandatory fields from API
                    const industryData = industriesData.find(i => i.name === templateData.industry);
                    if (templateData.industry && industryData) {
                        const mandatoryFields = industryData.mandatoryFields || [];

                        // Combine basic fields with mandatory fields, avoiding duplicates
                        const allFields = [...new Set([...mandatoryFields])];

                        setOffer({ fields: allFields });
                    } else {
                        // Fallback to basic fields if no industry data found
                        setOffer({ fields: ['Prénom', 'Nom', 'Téléphone', 'Email', 'Code postal'] });
                    }
                }
                // Mode 2: Offer-based mode (with optional template styling)
                else if (sellerId && offerId) {
                    setIsSideSellerMode(false);

                    // Fetch offer details
                    const offerRes = await axios.get(
                        `${CONFIG.apiUrl}/offer/public/${offerId}`
                    );
                    setOffer(offerRes.data);

                    // Fetch template if templateId is present (for styling)
                    if (templateId) {
                        const templateRes = await axios.get(`${CONFIG.apiUrl}/form-template/${templateId}`);
                        setTemplateStyle(templateRes.data);
                    }
                }
                // Missing required params
                else {
                    setError('Paramètres manquants: templateId seul OU (sellerId + offerId) sont requis');
                    setIsLoading(false);
                    return;
                }
            } catch (err: any) {
                console.error('Error fetching offer/template:', err);
                setError(
                    err.response?.data?.message || 
                    "Impossible de charger les informations de l'offre ou du template"
                );
            } finally {
                setIsLoading(false);
            }
        };

        fetchOfferAndTemplate();
    }, [sellerId, offerId, templateId, industriesData]);

    const handleSuccess = () => {
        setIsSubmitted(true);

        // Notify parent window if in iframe
        if (window.parent !== window) {
            window.parent.postMessage({ type: 'LEAD_FORM_SUCCESS' }, '*');
        }
    };

    const handleError = (error: any) => {
        console.error('Lead submission error:', error);
        toast.error(
            error.response?.data?.status || 
            "Une erreur s'est produite. Veuillez réessayer.",
            {
                position: 'top-center',
                autoClose: 5000,
            }
        );
    };

    if (isLoading || industriesLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-50">
                <div className="text-center">
                    <div className="mb-4 inline-block h-14 w-14 animate-spin rounded-full border-4 border-solid border-brand-500 border-r-transparent shadow-lg"></div>
                    <p className="text-base font-medium text-gray-700 animate-pulse">
                        Chargement du formulaire...
                    </p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-white dark:bg-navy-900 p-6">
                <div className="max-w-md text-center">
                    <div className="mb-4 text-6xl">⚠️</div>
                    <h2 className="mb-2 text-xl font-bold text-red-600 dark:text-red-400">
                        Erreur
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">{error}</p>
                </div>
            </div>
        );
    }

    if (!offer && !template) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-white dark:bg-navy-900 p-6">
                <div className="max-w-md text-center">
                    <div className="mb-4 text-6xl">🔍</div>
                    <h2 className="mb-2 text-xl font-bold text-gray-700 dark:text-gray-300">
                        {isSideSellerMode ? 'Template introuvable' : 'Offre introuvable'}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                        {isSideSellerMode 
                            ? "Le template demandé n'existe pas ou n'est plus disponible."
                            : "L'offre demandée n'existe pas ou n'est plus disponible."
                        }
                    </p>
                </div>
            </div>
        );
    }

    if (isSubmitted) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30 p-4 md:p-8 lg:p-12 flex items-center justify-center">
                <div className="mx-auto max-w-lg text-center animate-fade-in">
                    <div className="relative">
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-green-400 via-emerald-500 to-green-600 rounded-3xl opacity-20 blur-lg"></div>
                        <div className="relative rounded-3xl bg-white p-10 md:p-14 shadow-2xl border border-gray-100">
                            <div className="mb-6 text-6xl">&#10003;</div>
                            <h2 className="mb-3 text-2xl md:text-3xl font-bold text-gray-800">
                                Merci pour votre demande !
                            </h2>
                            <p className="text-gray-600 text-base md:text-lg">
                                Votre formulaire a bien été envoyé. Nous reviendrons vers vous dans les plus brefs délais.
                            </p>
                        </div>
                    </div>
                </div>

                <style>{`
                    @keyframes fade-in {
                        from { opacity: 0; transform: translateY(-10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .animate-fade-in {
                        animation: fade-in 0.6s ease-out;
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30 p-4 md:p-8 lg:p-12">
            <ToastContainer
                position="top-center"
                autoClose={4000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                toastClassName="!bg-white !shadow-2xl !rounded-2xl"
            />

            <div className="mx-auto max-w-2xl">

                {/* Header for side-seller mode showing industry */}
                {isSideSellerMode && template && (
                    <div className="mb-6 text-center animate-fade-in">
                        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
                            {template.industry}
                            {template.subIndustry && ` - ${template.subIndustry}`}
                        </h1>
                        <p className="text-gray-600">
                            Remplissez le formulaire pour recevoir des offres personnalisées
                        </p>
                    </div>
                )}

                {/* Form Container with Premium Design */}
                <div className="relative animate-slide-up">
                    {/* Decorative gradient border effect */}
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-brand-500 via-blue-500 to-brand-600 rounded-3xl opacity-20 blur-lg"></div>

                    <div className="relative rounded-3xl bg-white p-6 md:p-10 shadow-2xl border border-gray-100">
                        <LeadForm
                            sellerId={sellerId || undefined}
                            offerId={offerId || undefined}
                            templateId={templateId || undefined}
                            sideSellerId={sideSellerId || undefined}
                            isSideSellerMode={isSideSellerMode}
                            offerFields={offer?.fields || []}
                            formBuilder={formBuilder as any}
                            templateStyle={templateStyle}
                            onSuccess={handleSuccess}
                            onError={handleError}
                        />
                    </div>
                </div>

                {/* Footer Trust Elements */}
                <div className="mt-8 text-center space-y-3 animate-fade-in-delay-2">
                    <p className="text-xs text-gray-400">
                        En soumettant ce formulaire, vous acceptez d'être contacté par nos partenaires
                    </p>
                </div>
            </div>

            <style>{`
                @keyframes fade-in {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes slide-up {
                    from { opacity: 0; transform: translateY(30px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in {
                    animation: fade-in 0.6s ease-out;
                }
                .animate-fade-in-delay {
                    animation: fade-in 0.6s ease-out 0.2s both;
                }
                .animate-fade-in-delay-2 {
                    animation: fade-in 0.6s ease-out 0.4s both;
                }
                .animate-slide-up {
                    animation: slide-up 0.7s ease-out 0.3s both;
                }
            `}</style>
        </div>
    );
};

export default PublicLeadForm;
